import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import adminApi from './api'
import { MdCloudUpload } from 'react-icons/md'
import { Notification } from './components/Notification'

const MAX_FILE_SIZE_MB = 15

interface EventName {
  ru?: string
  ky?: string
  en?: string
}

interface Event {
  id: number
  name: string | EventName
  slug: string
}

export const MediaUploadForm: React.FC = () => {
  const navigate = useNavigate()
  const [events, setEvents] = useState<Event[]>([])
  const [uploading, setUploading] = useState(false)
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null)

  const [selectedEvent, setSelectedEvent] = useState<string>('')
  const [files, setFiles] = useState<File[]>([])
  const [videoUrls, setVideoUrls] = useState<string[]>([''])
  const [isDragging, setIsDragging] = useState(false)
  const [draggedFileIndex, setDraggedFileIndex] = useState<number | null>(null)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const fileUrlMapRef = useRef<Map<File, string>>(new Map())

  useEffect(() => {
    fetchEvents()
  }, [])

  // Cleanup для URL.createObjectURL при размонтировании
  useEffect(() => {
    const urlMap = fileUrlMapRef.current

    // Revoke old URLs that are no longer in files array
    const currentFiles = new Set(files)
    urlMap.forEach((url, file) => {
      if (!currentFiles.has(file)) {
        URL.revokeObjectURL(url)
        urlMap.delete(file)
      }
    })

    // Create URLs for new files
    files.forEach(file => {
      if (!urlMap.has(file)) {
        const url = URL.createObjectURL(file)
        urlMap.set(file, url)
      }
    })

    // Cleanup all URLs on unmount
    return () => {
      urlMap.forEach(url => URL.revokeObjectURL(url))
      urlMap.clear()
    }
  }, [files])

  const fetchEvents = async () => {
    try {
      const { data} = await adminApi.get('/api/admin/events')
      setEvents(data.events || [])
    } catch (err) {
      setNotification({ message: 'Ошибка загрузки списка событий', type: 'error' })
    }
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()

    // Валидация
    const errors: Record<string, string> = {}
    if (!selectedEvent) errors.selectedEvent = 'Обязательное поле'

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    const hasFiles = files.length > 0
    const hasVideos = videoUrls.some(url => url.trim() !== '')

    if (!hasFiles && !hasVideos) {
      setNotification({ message: 'Добавьте файлы или URL видео', type: 'error' })
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()

      // Добавляем файлы
      files.forEach(file => {
        formData.append('files', file)
      })

      // Добавляем URL видео
      const validVideoUrls = videoUrls.filter(url => url.trim() !== '')
      validVideoUrls.forEach(url => {
        formData.append('videoUrls', url.trim())
      })

      await adminApi.post(
        `/api/admin/events/${selectedEvent}/media`,
        formData
      )

      setNotification({ message: 'Медиа успешно загружено', type: 'success' })

      setTimeout(() => {
        navigate('/admin/media')
      }, 1500)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as any).response?.data?.message || 'Ошибка при загрузке')
        : 'Ошибка при загрузке'
      setNotification({ message: errorMessage, type: 'error' })
    } finally {
      setUploading(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const droppedFiles = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/')
    )

    if (droppedFiles.length > 0) {
      // Валидация размера файлов
      const maxSize = MAX_FILE_SIZE_MB * 1024 * 1024
      const tooLarge = droppedFiles.filter(file => file.size > maxSize)

      if (tooLarge.length > 0) {
        setNotification({
          message: `Файлы превышают максимальный размер ${MAX_FILE_SIZE_MB}MB: ${tooLarge.map(f => f.name).join(', ')}`,
          type: 'error'
        })
        return
      }

      setFiles(prev => [...prev, ...droppedFiles])
    }
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files)

      // Валидация размера файлов
      const maxSize = MAX_FILE_SIZE_MB * 1024 * 1024
      const tooLarge = selectedFiles.filter(file => file.size > maxSize)

      if (tooLarge.length > 0) {
        setNotification({
          message: `Файлы превышают максимальный размер ${MAX_FILE_SIZE_MB}MB: ${tooLarge.map(f => f.name).join(', ')}`,
          type: 'error'
        })
        return
      }

      setFiles(prev => [...prev, ...selectedFiles])
    }
  }

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleFileDragStart = (index: number) => {
    setDraggedFileIndex(index)
  }

  const handleFileDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedFileIndex === null || draggedFileIndex === index) return

    const newFiles = [...files]
    const draggedFile = newFiles[draggedFileIndex]

    newFiles.splice(draggedFileIndex, 1)
    newFiles.splice(index, 0, draggedFile)

    setFiles(newFiles)
    setDraggedFileIndex(index)
  }

  const handleFileDragEnd = () => {
    setDraggedFileIndex(null)
  }

  const addVideoUrlField = () => {
    setVideoUrls(prev => [...prev, ''])
  }

  const updateVideoUrl = (index: number, value: string) => {
    setVideoUrls(prev => prev.map((url, i) => i === index ? value : url))
  }

  const removeVideoUrl = (index: number) => {
    if (videoUrls.length > 1) {
      setVideoUrls(prev => prev.filter((_, i) => i !== index))
    } else {
      setVideoUrls([''])
    }
  }

  const getEventName = (eventName: string | EventName) => {
    if (!eventName) return '-'
    if (typeof eventName === 'object' && eventName.ru) {
      return eventName.ru
    }
    return String(eventName)
  }

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="page-header mb-6">
        <h1 className="page-title">Загрузка медиа</h1>
        <p className="page-subtitle">Добавьте изображения и видео для событий</p>
      </div>

      <div className="table-card">
        <form onSubmit={handleUpload} className="p-6">
          {/* Event Selection */}
          <div className="form-group mb-5">
            <label htmlFor="selectedEvent">
              Событие <span className="text-red-500">*</span>
            </label>
            <select
              id="selectedEvent"
              name="selectedEvent"
              value={selectedEvent}
              onChange={(e) => {
                setSelectedEvent(e.target.value)
                if (formErrors.selectedEvent) {
                  setFormErrors({ ...formErrors, selectedEvent: '' })
                }
              }}
              className={formErrors.selectedEvent ? 'form-control border-red-500' : 'form-control'}
              autoComplete="off"
            >
              <option value="">Выберите событие</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {getEventName(event.name)}
                </option>
              ))}
            </select>
            {formErrors.selectedEvent && (
              <div className="field-error">
                {formErrors.selectedEvent}
              </div>
            )}
          </div>

          {/* Video URLs */}
          <div className="mb-5">
            <div className="block mb-2 text-sm text-black/85">
              URL видео (YouTube)
            </div>
            {videoUrls.map((url, index) => (
              <div key={`video-${index}-${url}`} className="flex gap-2 mb-2">
                <input
                  id={`videoUrl${index}`}
                  type="url"
                  name={`videoUrl${index}`}
                  value={url}
                  onChange={(e) => updateVideoUrl(index, e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="form-control flex-1"
                  autoComplete="url"
                />
                {videoUrls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeVideoUrl(index)}
                    className="btn-danger px-3 py-2"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addVideoUrlField}
              className="bg-transparent border-0 text-[#1890ff] hover:text-[#40a9ff] cursor-pointer text-sm p-0"
            >
              + Добавить еще URL
            </button>
          </div>

          {/* Drag & Drop Zone for Images */}
          <div className="mb-5">
            <div className="block mb-2 text-sm text-black/85">
              Изображения
            </div>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              className={`rounded-lg p-10 text-center transition-all cursor-pointer border-2 border-dashed ${
                isDragging ? 'border-[#1890ff] bg-[#f0f7ff]' : 'border-[#d9d9d9] bg-[#fafafa]'
              }`}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              {React.createElement(MdCloudUpload as any, { size: 64, className: 'block mx-auto mb-4 text-[#1890ff]' })}
              <div className="text-base text-black/85 mb-2">
                Перетащите изображения сюда или нажмите для выбора
              </div>
              <div className="text-xs text-black/45">
                Максимальный размер файла: {MAX_FILE_SIZE_MB} MB
              </div>
              <input
                id="file-input"
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Selected Files */}
            {files.length > 0 && (
              <div className="mt-4">
                <div className="text-sm font-medium mb-3">
                  Выбрано файлов: {files.length}
                </div>
                <div
                  className="grid gap-3"
                  // dynamic: auto-fill grid without Tailwind's fixed cols API
                  style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}
                >
                  {files.map((file, index) => (
                    <div
                      key={`uploadfile-${file.name}-${file.size}-${index}`}
                      draggable
                      onDragStart={() => handleFileDragStart(index)}
                      onDragOver={(e) => handleFileDragOver(e, index)}
                      onDragEnd={handleFileDragEnd}
                      className={`relative aspect-square rounded-lg overflow-hidden cursor-move transition-all ${
                        draggedFileIndex === index ? 'border-2 border-dashed border-[#1890ff]' : 'border border-[#d9d9d9]'
                      }`}
                    >
                      <img
                        src={fileUrlMapRef.current.get(file) || ''}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-[#ff4d4f] border-0 text-white cursor-pointer text-sm flex items-center justify-center p-0"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-5 border-t border-gray-200">
            <button
              type="submit"
              disabled={uploading}
              className={`btn-primary ${uploading ? 'opacity-60' : ''}`}
            >
              {uploading ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/media')}
              className="btn-secondary"
              disabled={uploading}
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
